/**
 * Notas de crédito de clientes: se emiten sobre una factura emitida, por el total (la anula)
 * o por una parte (le deja saldo). Se registran en Control de Gestión con su folio de Softland.
 */
import { totalesDeItems } from "@/helpers/facturacion";

export type TipoNotaCredito = "total" | "parcial";
export type EstadoNotaCredito = "Borrador" | "Emitida" | "Anulada";

export interface ItemNotaCredito {
  glosa: string;
  montoNeto: number;
  /** Posición del ítem de la factura que se acredita. */
  indiceItem: number;
  division?: string;
  exento?: boolean;
}

export interface NotaCreditoFactura {
  id?: string;
  idFactura: string;
  folioFactura: number | null;
  codigoContrato: number | string;
  periodoFacturado: string;
  clienteSnapshot: { nombre: string; rut: string; [k: string]: unknown };
  tipo: TipoNotaCredito;
  motivo: string;
  items: ItemNotaCredito[];
  montoNeto: number;
  montoIva: number;
  montoExento?: number;
  montoTotal: number;
  estado: EstadoNotaCredito;
  /** Folio que asignó Softland a la nota de crédito. */
  folio?: number | null;
  fechaEmision?: string;
  creadoPor?: string;
  creadoEn?: string;
  historial?: Array<{ estado: EstadoNotaCredito; fecha: string; usuario: string; nota?: string }>;
}

interface ItemFactura {
  glosa: string;
  montoNeto: number;
  division?: string;
  exento?: boolean;
}

interface FacturaBase {
  id?: string;
  folio?: number | null;
  codigoContrato?: number | string;
  periodoFacturado: string;
  clienteSnapshot: { nombre: string; rut: string; [k: string]: unknown };
  items: ItemFactura[];
  montoNeto: number;
  montoTotal: number;
}

/** Notas de crédito vigentes (no anuladas) de una factura. */
export function notasDeFactura(notas: NotaCreditoFactura[], idFactura?: string): NotaCreditoFactura[] {
  return notas.filter((n) => n.idFactura === idFactura && n.estado !== "Anulada");
}

/** Neto ya acreditado de cada ítem de la factura (por posición). */
export function acreditadoPorItem(notas: NotaCreditoFactura[]): Record<number, number> {
  const r: Record<number, number> = {};
  for (const n of notas) for (const i of n.items) r[i.indiceItem] = (r[i.indiceItem] || 0) + (Number(i.montoNeto) || 0);
  return r;
}

/** Total (con IVA) que queda por cobrar de la factura después de sus notas de crédito. */
export function saldoFactura(factura: FacturaBase, notas: NotaCreditoFactura[]): number {
  const acreditado = notasDeFactura(notas, factura.id).reduce((s, n) => s + (Number(n.montoTotal) || 0), 0);
  return Math.max(0, Math.round(factura.montoTotal - acreditado));
}

/**
 * Ítems de la nota de crédito: total = todo lo que queda por acreditar de cada ítem;
 * parcial = los montos indicados por posición. Los ítems en $0 no se incluyen.
 */
export function itemsNotaCredito(
  factura: FacturaBase,
  tipo: TipoNotaCredito,
  notasPrevias: NotaCreditoFactura[],
  montosParciales: Record<number, number> = {}
): ItemNotaCredito[] {
  const previo = acreditadoPorItem(notasDeFactura(notasPrevias, factura.id));
  return factura.items
    .map((item, i) => {
      const disponible = Math.max(0, Math.round(item.montoNeto - (previo[i] || 0)));
      const monto = tipo === "total" ? disponible : Math.round(Number(montosParciales[i]) || 0);
      const nota: ItemNotaCredito = { glosa: `NC · ${item.glosa}`, montoNeto: monto, indiceItem: i };
      if (item.division) nota.division = item.division;
      if (item.exento) nota.exento = true;
      return nota;
    })
    .filter((i) => i.montoNeto > 0);
}

/** Arma la nota de crédito lista para guardar (sin id). */
export function armarNotaCredito(
  factura: FacturaBase,
  datos: { tipo: TipoNotaCredito; motivo: string; items: ItemNotaCredito[]; folio?: number | null }
): Omit<NotaCreditoFactura, "id" | "creadoPor" | "creadoEn" | "historial"> {
  const totales = totalesDeItems(datos.items);
  const emitida = !!datos.folio;
  return {
    idFactura: factura.id || "",
    folioFactura: factura.folio ?? null,
    codigoContrato: factura.codigoContrato ?? "",
    periodoFacturado: factura.periodoFacturado,
    clienteSnapshot: factura.clienteSnapshot,
    tipo: datos.tipo,
    motivo: datos.motivo.trim(),
    items: datos.items,
    montoNeto: totales.montoNeto,
    montoIva: totales.montoIva,
    montoExento: totales.montoExento || 0,
    montoTotal: totales.montoTotal,
    estado: emitida ? "Emitida" : "Borrador",
    folio: emitida ? datos.folio : null,
    ...(emitida ? { fechaEmision: new Date().toISOString() } : {}),
  };
}

/** Primer problema de la nota de crédito, o "" si se puede guardar. */
export function validarNotaCredito(
  factura: FacturaBase,
  datos: { tipo: TipoNotaCredito; motivo: string; items: ItemNotaCredito[]; folio?: number | null },
  todas: NotaCreditoFactura[]
): string {
  if (String(datos.motivo || "").trim().length < 5) return "Indica el motivo de la nota de crédito";
  if (!datos.items.length) return "La nota de crédito no tiene montos: indica cuánto se acredita";
  const previo = acreditadoPorItem(notasDeFactura(todas, factura.id));
  for (const item of datos.items) {
    const original = factura.items[item.indiceItem];
    const disponible = original ? original.montoNeto - (previo[item.indiceItem] || 0) : 0;
    if (item.montoNeto < 0 || item.montoNeto > disponible + 0.5)
      return `"${original ? original.glosa : "ítem"}" admite hasta ${Math.max(0, Math.round(disponible))} de neto`;
  }
  if (datos.folio !== undefined && datos.folio !== null) {
    if (!Number.isInteger(datos.folio) || datos.folio <= 0) return "El folio debe ser un número entero";
    const repetida = todas.find((n) => n.folio === datos.folio && n.estado !== "Anulada");
    if (repetida) return `El folio ${datos.folio} ya está en otra nota de crédito (${repetida.clienteSnapshot?.nombre})`;
  }
  return "";
}

/** La nota acredita todo lo que quedaba de la factura: con folio, la factura queda anulada. */
export function cubreTodaLaFactura(factura: FacturaBase, notas: NotaCreditoFactura[]): boolean {
  const neto = notasDeFactura(notas, factura.id).reduce((s, n) => s + (Number(n.montoNeto) || 0), 0);
  return neto >= factura.montoNeto - 0.5;
}
